<template>
    <el-config-provider :locale="locale">
      <div :style="styles">
        <div v-if="platformEnterError" class="platform-enter-error">
          <div class="platform-enter-error-title">Arcubase 自动登录失败</div>
          <div class="platform-enter-error-message">{{ platformEnterError }}</div>
        </div>
        <router-view v-else-if="shouldRenderRouterView" />
      </div>
    </el-config-provider>
    <iframe name="download" style="display:none"></iframe>
</template>
<script setup lang="ts">
import { ref, provide, onBeforeMount, onMounted, onErrorCaptured, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ElNotification } from 'element-plus'
import { useUserStore } from '@/store/user'
import { setLocale, initLocale, setupLocale } from '@/utils/common'
import { exchangeAuthLoginCode } from '@/api/auth/login'
import { getOrCreateBrowserIdentHash } from '@/utils/browserIdent'
import { commitAuthResponse } from '@/auth/login'
import {
  getPlatformEnterFailureMessage,
  getPlatformEnterLoginCodeFromHash,
  shouldRenderPlatformEnterRouter,
} from '@/auth/platformEnter'

// Reactive data
const styles = ref<Record<string, any>>({})
const locale = ref([])
const i18nInstance = ref<any>(null)
const platformEnterError = ref('')
const appReady = ref(false)
const shouldRenderRouterView = computed(() => shouldRenderPlatformEnterRouter({
  appReady: appReady.value,
  platformEnterError: platformEnterError.value,
}))

// Composables
const { t } = useI18n()
const router = useRouter()
const store = useUserStore()

// Error handling interface
interface CustomError extends Error {
  key?: string
  type?: string
  addon?: any
}

// Error captured handler
const errorCaptured = (err: CustomError): boolean => {
  // Handle specific Vue runtime errors
  if (err.message) {
    // Handle null reference errors
    if (err.message.includes("Cannot read properties of undefined") ||
        err.message.includes("Cannot read properties of null") ||
        err.message.includes("Cannot destructure property")) {
      console.warn('Null reference error caught:', err.message)
      // Don't show error to user for null reference errors in production
      if (import.meta.env.PROD) {
        return false
      }
    }

    // Handle parentNode errors
    if (err.message.includes("Cannot read properties of null (reading 'parentNode')")) {
      console.warn('DOM parentNode error caught:', err.message)
      return false
    }
  }

  if (err.key && err.type && err.type === 'user') {
    $message(t('error.' + err.key, err.addon), 'error')
    return false
  }
  if (import.meta.env.PROD) {
    return false
  }
  return true
}

// Custom message function
const $message = (msg: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  ElNotification({
    title: t(type),
    message: msg,
    type: type,
  })
}

const consumePlatformEnterLoginCodeFromHash = async () => {
  const loginCode = getPlatformEnterLoginCodeFromHash(window.location.hash)
  if (!loginCode) return true

  try {
    const data = await exchangeAuthLoginCode({
      code: loginCode,
      browser_ident_hash: getOrCreateBrowserIdentHash(),
    })
    await commitAuthResponse(data)
  } catch (error) {
    platformEnterError.value = getPlatformEnterFailureMessage(error)
    console.error('Platform enter login failed', error)
    return false
  }

  const cleanUrl = window.location.pathname + window.location.search
  window.history.replaceState({}, '', cleanUrl)
  return true
}

// Initialize locale
const initLocaleWrapper = async () => {
  if (!store.haslogin && window.location.pathname.indexOf('/auth') !== 0) {
    let redirect = window.location.pathname
    if (window.location.search) {
      redirect += encodeURIComponent(window.location.search)
    }
    router.push('/auth/passport?redirect_uri=' + redirect)
    await initLocale()
  } else {
    const userLocale = store.user?.locale
    await initLocale(userLocale)
  }
}

// Wrapper function for child components
const setLocaleWrapper = async (lang: string) => {
  await setLocale(lang)
}

// Lifecycle hooks
onBeforeMount(async () => {
  i18nInstance.value = useI18n()
  setupLocale(i18nInstance.value, locale)
  const platformEnterReady = await consumePlatformEnterLoginCodeFromHash()
  if (!platformEnterReady) return
  await initLocaleWrapper()
  appReady.value = true
})

onMounted(() => {
  // this.$eventBus.on('systemIsReady', ()=>{
  //   document.getElementById('app').style.visibility = 'visible';
  //   document.getElementById('mainloading').style.display = 'none';
  // })

  // Global error handlers for unhandled errors
  window.addEventListener('error', (event) => {
    console.warn('Global error caught:', event.error)
    if (event.error?.message?.includes('Cannot read properties of null') ||
        event.error?.message?.includes('Cannot read properties of undefined')) {
      event.preventDefault()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    console.warn('Unhandled promise rejection:', event.reason)
    if (event.reason?.message?.includes('Cannot read properties of null') ||
        event.reason?.message?.includes('Cannot read properties of undefined')) {
      event.preventDefault()
    }
  })
})

// Error handling
onErrorCaptured((err: unknown) => {
  return errorCaptured(err as CustomError)
})

// const store = inject('$store') as any;
// const loading = inject('$loading') as any;
// const message = inject('$message') as any;
// const setLocale = inject('setLocale') as any;
// Provide setLocale function to child components
provide('setLocale', setLocaleWrapper)
provide('$t', t)
provide('$message', $message)
provide('$store', store)
</script>
<style lang="scss">
@import '@/config/variables.scss';
@import './formStyle.scss';

body{
  margin: 0;
}
a:visited{
  color: #666;
}
.none-enter-active,
.none-leave-active {
  transition: opacity 0s ease;
}

.highlight-yellowfade{
  animation: yellowfade 2s;
}

.platform-enter-error {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;
  background: #f8fafc;
  color: #1f2937;
  text-align: center;
}

.platform-enter-error-title {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
}

.platform-enter-error-message {
  max-width: 560px;
  line-height: 1.7;
  color: #4b5563;
}

@keyframes yellowfade {
    0% { background-color: rgba(255,255,180, 1) }
    100% { background-color: rgba(255,255,255,0) }
}
</style>
